import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useMyTeamOnboardingApi from './config';
import DataTable from '../../components/ui/DataTable';
import { formatDate } from '../../utils/Utils';
import PersonCell from '../../components/ui/PersonCell';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';

const Container = styled.div``;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0, 42, 42, 0.4);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center; justify-content: center; z-index: 1300; padding: 24px;
`;
const Card = styled.div`
  background: #fff; border-radius: 10px; max-width: 720px; width: 100%;
  max-height: 90vh; overflow-y: auto; padding: 26px 28px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;
const CardTitle = styled.h3`
  margin: 0 0 4px; font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const CardSub = styled.p` margin: 0 0 18px; color: ${({ theme }) => theme.colors.text.dark.little}; font-size: 13px; `;

const SummaryGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px 20px; margin-bottom: 20px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const Cell = styled.div` display: flex; flex-direction: column; gap: 2px; `;
const CellLabel = styled.span` font-size: 12px; color: ${({ theme }) => theme.colors.text.dark.little}; `;
const CellValue = styled.span` font-size: 14px; color: ${({ theme }) => theme.colors.text.primary}; font-weight: 600; `;

const SectionTitle = styled.h4`
  margin: 18px 0 8px; font-size: 14px; color: ${({ theme }) => theme.colors.text.primary};
`;
const TaskRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 8px 0; border-bottom: 1px solid #EEF1F4; font-size: 13px;
`;
const TaskMeta = styled.span` color: ${({ theme }) => theme.colors.text.dark.little}; font-size: 12px; `;

// Onay bekleyen görev satırı: metin + rozet TEK satırda kalamaz (iki düğme
// eklenir), bu yüzden satır dikeye açılır ve düğmeler alt sıraya iner.
const TaskDecision = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 0 0 10px; margin-top: -2px;
`;
const DecisionNote = styled.div` flex: 1 1 220px; min-width: 200px; `;

// Kronoloji satırı: tarih sütunu + nokta + olay içeriği (dikey akış).
const TimelineRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid #F0F2F5;

  &:last-of-type { border-bottom: none; }
`;

const TimelineDate = styled.span`
  flex: 0 0 84px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 12px;
  padding-top: 1px;
`;

const TimelineDot = styled.span`
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.main};
  margin-top: 5px;
`;
const StatusPill = styled.span`
  font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; white-space: nowrap;
  background: ${({ $done, theme }) => ($done ? `${theme.colors.auxiliar.success}22` : '#EEF1F4')};
  color: ${({ $done, theme }) => ($done ? theme.colors.auxiliar.success : theme.colors.text.dark.little)};
`;
const Actions = styled.div` display: flex; justify-content: flex-end; margin-top: 20px; `;
const CloseBtn = styled.button`
  background: #fff; color: ${({ theme }) => theme.colors.text.dark.little}; border: 1px solid #cbd5e0;
  border-radius: 4px; padding: 8px 24px; font-size: 14px; cursor: pointer;
  &:hover { background: #f1f5f9; }
`;

export default function MyTeamOnboardingsPage() {
  const { t, i18n } = useTranslation();
  const { loading, rows, refresh, getDetail, approveTask, rejectTask } = useMyTeamOnboardingApi();
  const [detail, setDetail] = useState(null); // { onboarding, tasks, timeline }
  const [detailLoading, setDetailLoading] = useState(false);
  // OBB-09 — karar durumu. `decidingId` açık not kutusunu, `busyId` uçuşta olan
  // isteği tutar; ikisi ayrı çünkü not yazarken satır kilitlenmemeli.
  const [decision, setDecision] = useState(null); // { taskId, mode: 'approve'|'reject', note }
  const [busyTaskId, setBusyTaskId] = useState(null);

  const statusLabel = useCallback((s) => t(`onboarding.status.${s}`, { defaultValue: s }), [t]);
  const periodLabel = useCallback((s) => t(`onboarding.period.${s}`, { defaultValue: s }), [t]);
  const riskLabel = useCallback((s) => t(`onboarding.riskLevel.${s}`, { defaultValue: s }), [t]);

  const openDetail = useCallback(async (row) => {
    setDetailLoading(true);
    setDecision(null);
    setDetail({ onboarding: row, tasks: [] });
    try {
      const { detail: d, tasks, timeline } = await getDetail(row.id);
      setDetail({ onboarding: d || row, tasks, timeline });
    } finally {
      setDetailLoading(false);
    }
  }, [getDetail]);

  // Karardan sonra AÇIK OLAN detayı sunucudan yeniden okur: görev listesi ve
  // ilerleme yüzdesi (yalnız `completed` sayar) aynı anda güncellensin. Liste de
  // tazelenir — bekleyen onay aşaması kolonu karara bağlıdır.
  const decide = useCallback(async (task, mode, note) => {
    const onboardingId = detail?.onboarding?.id;
    if (!onboardingId) return;
    setBusyTaskId(task.id);
    const ok = mode === 'approve'
      ? await approveTask(onboardingId, task.id, note)
      : await rejectTask(onboardingId, task.id, note);
    if (ok) {
      const { detail: d, tasks, timeline } = await getDetail(onboardingId);
      setDetail((prev) => ({ onboarding: d || prev?.onboarding, tasks, timeline }));
      setDecision(null);
      refresh();
    }
    setBusyTaskId(null);
  }, [detail, approveTask, rejectTask, getDetail, refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'personFullName', label: t('onboarding.person'), options: {
      filter: false, sort: true,
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={rows[tableMeta.rowIndex]?.personProfilePicture} />
      ),
    } },
    {
      name: 'status',
      label: t('onboarding.statusLabel'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          const stage = row && row.pendingApprovalStage;
          const base = statusLabel(v);
          if (!stage) return base;
          const stageText = t(`onboarding.pendingStage.${stage}`, { defaultValue: '' });
          const who = row.pendingApproverName;
          return (
            <div>
              <div>{base}</div>
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                {who ? `${stageText}: ${who}` : stageText}
              </div>
            </div>
          );
        },
      },
    },
    { name: 'currentPeriod', label: t('onboarding.periodLabel'), options: { filter: false, sort: true, customBodyRender: (v) => periodLabel(v) } },
    { name: 'earlyLeaveRiskLevel', label: t('onboarding.riskLabel'), options: { filter: false, sort: true, customBodyRender: (v) => (v ? riskLabel(v) : '-') } },
    { name: 'startDate', label: t('onboarding.startDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, i18n.language) } },
    { name: 'expectedDay90', label: t('onboarding.expectedDay90'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, i18n.language) } },
    {
      name: 'detail', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return (
            <button
              type="button"
              onClick={() => openDetail(row)}
              style={{ background: 'none', border: 'none', color: '#0291EF', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
            >
              {t('myTeamOnboarding.viewDetail')}
            </button>
          );
        },
      },
    },
  ], [t, i18n.language, rows, statusLabel, periodLabel, riskLabel, openDetail]);

  const ob = detail?.onboarding;

  return (
    <Container>
      <DataTable
        title={t('myTeamOnboarding.pageTitle')}
        fetching={loading}
        columns={columns}
        data={rows}
        totalRowCount={rows.length}
        pageSize={25}
        pageIndex={0}
        disableFilters
      />

      <Overlay $open={!!detail} onClick={() => setDetail(null)}>
        {detail && (
          <Card onClick={(e) => e.stopPropagation()}>
            <CardTitle>{ob?.personFullName || '...'}</CardTitle>
            <CardSub>{ob?.personEmail}</CardSub>

            <SummaryGrid>
              <Cell><CellLabel>{t('onboarding.statusLabel')}</CellLabel><CellValue>{statusLabel(ob?.status)}</CellValue></Cell>
              <Cell><CellLabel>{t('onboarding.periodLabel')}</CellLabel><CellValue>{periodLabel(ob?.currentPeriod)}</CellValue></Cell>
              <Cell><CellLabel>{t('onboarding.startDate')}</CellLabel><CellValue>{formatDate(ob?.startDate, i18n.language)}</CellValue></Cell>
              <Cell><CellLabel>{t('onboarding.expectedDay90')}</CellLabel><CellValue>{formatDate(ob?.expectedDay90, i18n.language)}</CellValue></Cell>
              <Cell><CellLabel>{t('onboarding.riskLabel')}</CellLabel><CellValue>{ob?.earlyLeaveRiskLevel ? riskLabel(ob.earlyLeaveRiskLevel) : '-'}</CellValue></Cell>
              <Cell><CellLabel>{t('myTeamOnboarding.latestNps')}</CellLabel><CellValue>{ob?.day90NpsScore ?? ob?.day60NpsScore ?? ob?.day30NpsScore ?? '-'}</CellValue></Cell>
            </SummaryGrid>

            {ob?.pendingApprovalStage && (
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#9a3412', marginBottom: 8 }}>
                {t(`onboarding.pendingStage.${ob.pendingApprovalStage}`, { defaultValue: '' })}
                {ob.pendingApproverName ? `: ${ob.pendingApproverName}` : ''}
              </div>
            )}

            {/* Erken kapanış gerekçesi: "tamamlandı ama beklenen 90. gün ileride"
                çelişkisinin cevabı — müşteri geri bildirimi (2026-08). */}
            {ob?.earlyCompletionReason && (
              <div style={{ background: '#FEFCE8', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#854D0E', marginBottom: 8 }}>
                <strong>{t('onboarding.earlyCompletionReason')}:</strong> {ob.earlyCompletionReason}
              </div>
            )}

            {/* Kronoloji — müşteri geri bildirimi (2026-08): "kronoloji yok, en son
                durum görünüyor sadece". Dönem onayı yaşam döngüsü, 1:1'ler, uzatma
                ve kapanış/iptal gerekçeleri zaman sırasıyla (tarihsiz uzatma sonda). */}
            <SectionTitle>{t('myTeamOnboarding.timeline')}</SectionTitle>
            {detailLoading && <TaskMeta>{t('common.loading', { defaultValue: 'Yükleniyor...' })}</TaskMeta>}
            {!detailLoading && (detail.timeline || []).map((ev, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TimelineRow key={i}>
                <TimelineDate>{ev.date ? formatDate(ev.date, i18n.language) : '—'}</TimelineDate>
                <TimelineDot />
                <span>
                  <strong>{t(`myTeamOnboarding.timelineEvent.${ev.type}`, { defaultValue: ev.type })}</strong>
                  {ev.period && <TaskMeta style={{ marginLeft: 6 }}>{periodLabel(ev.period)}</TaskMeta>}
                  {ev.actor && <TaskMeta style={{ marginLeft: 6 }}>· {ev.actor}</TaskMeta>}
                  {ev.nps != null && <TaskMeta style={{ marginLeft: 6 }}>· NPS: {ev.nps}</TaskMeta>}
                  {ev.note && (
                    <TaskMeta style={{ display: 'block', marginTop: 2 }}>
                      {ev.type === 'one_on_one'
                        ? t(`onboardingOneOnOne.status.${ev.note}`, { defaultValue: ev.note })
                        : ev.note}
                    </TaskMeta>
                  )}
                </span>
              </TimelineRow>
            ))}

            <SectionTitle>{t('myTeamOnboarding.tasks')}</SectionTitle>
            {detailLoading && <TaskMeta>{t('common.loading', { defaultValue: 'Yükleniyor...' })}</TaskMeta>}
            {!detailLoading && (detail.tasks || []).length === 0 && <TaskMeta>{t('myTeamOnboarding.noTasks')}</TaskMeta>}
            {(detail.tasks || []).map((task) => {
              // OBB-09 — çalışanın kapattığı her görev buraya düşer
              // (`manager_approval_pending`). Kararı YALNIZ sürecin yöneticisi
              // verebilir; mentör aynı listeyi görür ama düğmeleri görmez
              // (sunucu CanApproveTasks der — 403'le biten düğme göstermeyiz).
              const pending = task.status === 'manager_approval_pending';
              const canDecide = pending && !!ob?.canApproveTasks;
              const open = decision && decision.taskId === task.id;
              const busy = busyTaskId === task.id;
              return (
                <React.Fragment key={task.id}>
                  <TaskRow>
                    <span>
                      {task.title}
                      <TaskMeta style={{ marginLeft: 8 }}>
                        {periodLabel(task.period)} · {t(`myTeamOnboarding.ownerRole.${task.ownerRole}`, { defaultValue: task.ownerRole })}
                      </TaskMeta>
                    </span>
                    <StatusPill $done={task.status === 'completed'}>
                      {t(`myTeamOnboarding.taskStatus.${task.status}`, { defaultValue: task.status })}
                    </StatusPill>
                  </TaskRow>
                  {canDecide && !open && (
                    <TaskDecision>
                      <Button variant="primary" disabled={busy}
                        onClick={() => setDecision({ taskId: task.id, mode: 'approve', note: '' })}>
                        {t('myTeamOnboarding.taskAction.approve')}
                      </Button>
                      <Button variant="outline" disabled={busy}
                        onClick={() => setDecision({ taskId: task.id, mode: 'reject', note: '' })}>
                        {t('myTeamOnboarding.taskAction.reject')}
                      </Button>
                    </TaskDecision>
                  )}
                  {canDecide && open && (
                    <TaskDecision>
                      <DecisionNote>
                        <TextInput
                          type="textarea" rows={2}
                          label={decision.mode === 'reject'
                            ? t('myTeamOnboarding.taskAction.rejectNote')
                            : t('myTeamOnboarding.taskAction.approveNote')}
                          value={decision.note}
                          onChange={(e) => setDecision({ ...decision, note: e.target.value })}
                        />
                      </DecisionNote>
                      <Button
                        variant="primary"
                        // Red bir GERİ GÖNDERMEDİR: görev `in_progress`e döner ve
                        // çalışan neyi düzelteceğini yalnız bu nottan öğrenir —
                        // bu yüzden redde not ZORUNLU, onayda isteğe bağlıdır.
                        disabled={busy || (decision.mode === 'reject' && !decision.note.trim())}
                        onClick={() => decide(task, decision.mode, decision.note.trim())}>
                        {busy ? '...' : t('common.confirm')}
                      </Button>
                      <Button variant="ghost" disabled={busy} onClick={() => setDecision(null)}>
                        {t('common.cancel')}
                      </Button>
                    </TaskDecision>
                  )}
                </React.Fragment>
              );
            })}

            <Actions>
              <CloseBtn onClick={() => setDetail(null)}>{t('common.close', { defaultValue: 'Kapat' })}</CloseBtn>
            </Actions>
          </Card>
        )}
      </Overlay>
    </Container>
  );
}
